import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { listMyReports } from "@/lib/report-card.functions";
import { SOURCE_LABEL, type AIReportCard } from "@/lib/report-card";
import { FileText, TrendingUp, TrendingDown, Sparkles, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/learner/reports")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "AI Report Cards — MAKAI" },
      { name: "description", content: "AI-powered learning reports from Makai — see strengths, areas to improve, and your next best steps after every quiz, paper and topic." },
      { property: "og:title", content: "AI Report Cards — MAKAI" },
      { property: "og:description", content: "Personalised AI reports for every quiz, paper and topic — with a clear next-learning plan." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ReportsPage,
});

function ReportsPage() {
  const list = useServerFn(listMyReports);
  const { data, isLoading } = useQuery({ queryKey: ["ai-reports"], queryFn: () => list() });
  const reports = (data ?? []) as AIReportCard[];

  return (
    <AppShell role="learner" title="AI Report Cards">
      <div className="max-w-4xl mx-auto space-y-4 p-4">
        <header className="rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 p-5 border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Sparkles className="h-4 w-4" /> MAKAI understands how you learn</div>
          <h1 className="text-2xl font-semibold mt-1">Your AI report cards</h1>
          <p className="text-sm text-muted-foreground mt-1">A personalised report after every quiz, paper and topic. See what you know, what to practise, and your next best step.</p>
        </header>

        {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && reports.length === 0 && (
          <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">
            No reports yet. Finish a quiz or a past paper — Makai will generate a report for you.
          </div>
        )}

        {reports.length > 1 && <TrendCard reports={reports} />}

        <div className="grid gap-3">
          {reports.map((r) => (
            <Link
              key={r.id}
              to="/learner/reports/$reportId"
              params={{ reportId: r.id }}
              className="rounded-xl border p-4 hover:bg-accent/40 transition flex items-start gap-3"
            >
              <div className="rounded-lg bg-primary/10 p-2 text-primary"><FileText className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="px-1.5 py-0.5 rounded bg-muted">{SOURCE_LABEL[r.source_type]}</span>
                  {r.subject && <span>· {r.subject}</span>}
                  <span>· {new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <div className="font-medium truncate">{r.title}</div>
                <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span>Score <span className="font-medium text-foreground">{r.percent ?? 0}%</span></span>
                  <span>Grade <span className="font-medium text-foreground">{r.grade ?? "—"}</span></span>
                  <span>Mastery <span className="font-medium text-foreground">{r.mastery_level ?? "—"}</span></span>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground self-center" />
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function TrendCard({ reports }: { reports: AIReportCard[] }) {
  const latest = reports[0];
  const prev = reports[1];
  if (!latest || !prev || latest.percent == null || prev.percent == null) return null;
  const delta = +(latest.percent - prev.percent).toFixed(1);
  const up = delta >= 0;
  return (
    <div className="rounded-xl border p-4 bg-card">
      <div className="text-xs text-muted-foreground">Since your last report</div>
      <div className={`mt-1 flex items-center gap-2 text-lg font-medium ${up ? "text-emerald-600" : "text-amber-600"}`}>
        {up ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
        {up ? "+" : ""}{delta}% ({prev.percent}% → {latest.percent}%)
      </div>
      <div className="text-sm text-muted-foreground mt-1">
        {up ? "Nice — your recent work is improving." : "Keep pushing — Makai's next plan will help you climb again."}
      </div>
    </div>
  );
}
